/**
 * Menu bergrup (#1758, keputusan Owner 24 Okt 2026) — MURNI, tanpa React.
 *
 * Owner/Admin sebelumnya melihat 16 menu sejajar, dengan tumpang-tindih yang menuntut orang tahu
 * bedanya lebih dulu: "Tugas" vs "Kelola Tugas", Beranda vs Laporan, Alat/Alert/Aturan alert
 * terpisah tiga, dan menu setup sekali-pakai sejajar dengan menu harian.
 *
 * Sekarang layar yang SAMA dikelompokkan menjadi grup. Satu grup = satu entri menu; layar-layar di
 * dalamnya menjadi tab di atas halamannya (`HubTabs`). Tidak ada layar yang dipindah, diganti
 * rute, atau dihapus — yang berubah hanya cara menawarkannya.
 *
 * `visibleNavItems` (navigation.ts) TETAP satu-satunya penyaring izin: grup hanya dibangun dari
 * menu yang SUDAH lolos saringan itu, jadi grup tidak pernah dapat menawarkan layar yang tidak
 * boleh dibuka. Grup yang seluruh isinya tersaring ikut hilang.
 */

import type { NavItem } from './navigation';

export type HubKey =
  'beranda' | 'laporan' | 'absen' | 'tugas' | 'absensi' | 'karyawan' | 'alat' | 'pengaturan';

export interface NavHub {
  readonly key: HubKey;
  readonly label: string;
  /** Tujuan saat menu grup diketuk = layar pertama yang boleh dibuka di grup itu. */
  readonly to: string;
  /** Layar di dalam grup, berurutan; `label` di sini adalah label TAB. */
  readonly members: readonly NavItem[];
}

/** Urutan grup di menu. Empat pertama yang tersedia mengisi bilah bawah. */
const URUTAN_HUB: readonly HubKey[] = [
  'beranda',
  'laporan',
  'absen',
  'tugas',
  'absensi',
  'karyawan',
  'alat',
  'pengaturan',
];

/** Urutan layar (tab) di dalam tiap grup. */
const ANGGOTA: Readonly<Record<HubKey, readonly string[]>> = {
  beranda: ['/'],
  laporan: ['/laporan'],
  absen: ['/attendance/check'],
  tugas: ['/tasks/manage', '/tasks'],
  absensi: ['/attendance/check', '/attendance/history', '/attendance/corrections', '/laporan'],
  karyawan: ['/workers', '/teams'],
  alat: ['/assets', '/alerts'],
  pengaturan: ['/work-locations', '/attendance-policy', '/task-settings', '/alert-rules', '/paket'],
};

/**
 * Label tab yang berbeda dari label menunya — di dalam grup "Tugas", dua tab bernama "Tugas" dan
 * "Kelola Tugas" mengulang kebingungan yang justru ingin dihapus.
 */
const LABEL_TAB: Readonly<Record<string, string>> = {
  '/tasks/manage': 'Semua tugas',
  '/tasks': 'Tugas saya',
};

/**
 * "Absen" (layar check-in) adalah tombol UTAMA bagi pekerja lapangan — ia tetap berdiri sendiri
 * di bilah bawahnya. Bagi Owner/Manajer ia hanya salah satu tab di grup Absensi; menempatkannya
 * sejajar di menu pengawas justru yang membuat Beranda mereka terasa seperti aplikasi kurir.
 */
function hubUntukRute(to: string, oversight: boolean): HubKey | null {
  if (to === '/attendance/check') return oversight ? 'absensi' : 'absen';
  /*
   * Laporan: MENU SENDIRI bagi pengawas (keputusan Owner 24 Okt 2026, #1760 — membalik #1758 yang
   * menaruhnya sebagai tab di Beranda, karena Owner tidak menemukannya di sana: "dimana fitur
   * laporan?"). Bagi pekerja lapangan tetap tab di grup Riwayat — Laporan mereka berisi absensi &
   * tugas MILIKNYA sendiri, dan Beranda mereka dijanjikan tidak berubah (review independen N2).
   */
  if (to === '/laporan') return oversight ? 'laporan' : 'absensi';
  for (const key of URUTAN_HUB) {
    if (key === 'absen') continue;
    if (ANGGOTA[key].includes(to)) return key;
  }
  return null;
}

function labelHub(key: HubKey, oversight: boolean): string {
  switch (key) {
    case 'beranda':
      return 'Beranda';
    case 'laporan':
      return 'Laporan';
    case 'absen':
      return 'Absen';
    case 'tugas':
      return 'Tugas';
    case 'absensi':
      // Bagi pekerja lapangan grup ini isinya Riwayat + Koreksi miliknya sendiri; "Absensi" di
      // samping "Absen" di bilah yang sama hanya akan dibaca sebagai dua tombol untuk hal yang sama.
      return oversight ? 'Absensi' : 'Riwayat';
    case 'karyawan':
      return 'Karyawan';
    case 'alat':
      return 'Alat';
    case 'pengaturan':
      return 'Pengaturan';
  }
}

/**
 * Kelompokkan menu yang SUDAH disaring izin. Menu yang tidak dikenal grup mana pun tidak hilang:
 * ia jatuh ke grup Pengaturan sebagai tab terakhir — lebih baik terselip daripada tak terjangkau.
 */
export function navHubs(visible: readonly NavItem[], oversight: boolean): readonly NavHub[] {
  const isi = new Map<HubKey, NavItem[]>();
  for (const item of visible) {
    const key = hubUntukRute(item.to, oversight) ?? 'pengaturan';
    const daftar = isi.get(key) ?? [];
    daftar.push({ ...item, label: LABEL_TAB[item.to] ?? item.label });
    isi.set(key, daftar);
  }

  const hasil: NavHub[] = [];
  for (const key of URUTAN_HUB) {
    const anggota = isi.get(key);
    if (!anggota || anggota.length === 0) continue;
    const urutan = ANGGOTA[key];
    const terurut = [...anggota].sort((a, b) => peringkat(urutan, a.to) - peringkat(urutan, b.to));
    hasil.push({
      key,
      label: labelHub(key, oversight),
      to: (terurut[0] as NavItem).to,
      members: terurut,
    });
  }
  return hasil;
}

function peringkat(urutan: readonly string[], to: string): number {
  const i = urutan.indexOf(to);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/**
 * Bilah bawah: EMPAT grup + tombol "Lainnya" yang SELALU ada.
 *
 * Selalu, bukan hanya saat meluap: halaman Lainnya juga rumah tombol Keluar. Kurir yang grupnya
 * pas empat (Beranda · Absen · Tugas · Riwayat) dulu tetap punya luapan (Koreksi, Laporan, Paket)
 * sehingga tombol itu kebetulan muncul; setelah pengelompokan luapannya kosong, dan tanpa aturan
 * ini kurir kehilangan satu-satunya jalan keluar dari akunnya di ponsel.
 */
export const SLOT_GRUP_BAWAH = 4;

export function tataLetakBawah(hubs: readonly NavHub[]): {
  readonly primary: readonly NavHub[];
  readonly overflow: readonly NavHub[];
  /**
   * Apakah tombol "Lainnya" dirender — keputusannya DI SINI, bukan di JSX `BottomNav`, supaya
   * dapat diuji (temuan review independen R2: aturan ini sebelumnya hidup sebagai JSX tanpa
   * syarat, dan mengembalikan syarat lama "hanya saat meluap" lolos semua uji).
   */
  readonly tombolLainnya: boolean;
} {
  return {
    primary: hubs.slice(0, SLOT_GRUP_BAWAH),
    overflow: hubs.slice(SLOT_GRUP_BAWAH),
    tombolLainnya: true,
  };
}

/**
 * Layar laporan yang bukan anggota grup mana pun (dibuka dari kartu Ringkasan) — menyalakan grup
 * yang MEMUAT `/laporan`, apa pun grupnya bagi peran itu (Laporan bagi pengawas, Riwayat bagi
 * pekerja lapangan). Hanya untuk menyalakan menu yang benar, tidak untuk tab.
 */
const AWALAN_LAPORAN: readonly string[] = ['/dashboard', '/deliveries'];

function cocokAwalan(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Grup mana yang sedang dibuka — dicocokkan ke awalan rute TERPANJANG, supaya `/tasks/manage`
 * tidak terbaca sebagai `/tasks` dan `/tasks/123` (detail) tetap menyalakan grup Tugas.
 */
export function hubAktif(hubs: readonly NavHub[], pathname: string): HubKey | null {
  let terbaik: { key: HubKey; panjang: number } | null = null;
  for (const hub of hubs) {
    for (const m of hub.members) {
      if (cocokAwalan(pathname, m.to) && (terbaik === null || m.to.length > terbaik.panjang)) {
        terbaik = { key: hub.key, panjang: m.to.length };
      }
    }
  }
  if (terbaik) return terbaik.key;
  if (AWALAN_LAPORAN.some((awalan) => cocokAwalan(pathname, awalan))) {
    return hubs.find((h) => h.members.some((m) => m.to === '/laporan'))?.key ?? null;
  }
  return null;
}

/**
 * Tab yang ditampilkan di atas halaman — HANYA di layar daftar anggota grup itu sendiri (cocok
 * persis), bukan di layar detail atau formulir di bawahnya, dan hanya bila grupnya berisi lebih
 * dari satu layar. Tab tunggal bukan pilihan, hanya garis tambahan.
 */
export function tabUntuk(
  hubs: readonly NavHub[],
  pathname: string,
): { readonly hub: NavHub; readonly aktif: string } | null {
  for (const hub of hubs) {
    if (hub.members.length < 2) continue;
    const cocok = hub.members.find((m) => m.to === pathname);
    if (cocok) return { hub, aktif: cocok.to };
  }
  return null;
}
